import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import cx from 'classnames';

import BaseSlider from 'components/sliders/BaseSlider';
import DataLoader from 'components/DataLoader';
import Tag from 'components/Tag';

import styles from './styles.module.scss';

const TagsModal = dynamic(() => import('components/modals/Tags'), {
	ssr: false,
	loading: DataLoader
});

const FilterTags = ({
	tags,
	selectedTag,
	basePath,
	asPath,
	numberVisibleTags,
	titleMoreButton
}) => {
	const [isShowModal, setIsShowModal] = useState(false);

	const [showRightGradient, setShowRightGradient] = useState(true);
	const [showLeftGradient, setShowLeftGradient] = useState(true);

	const [slideActiveIndex, setSlideActiveIndex] = useState(0);

	const tagsByVisibility = useMemo(() => {
		let preparedTags = [...tags];

		if (selectedTag && preparedTags.length > numberVisibleTags) {
			const tagIndex = preparedTags.findIndex(
				(tag) => tag.name === selectedTag
			);

			if (tagIndex !== -1 && tagIndex >= numberVisibleTags) {
				preparedTags = preparedTags.reduce(
					(memo, key, index) => {
						if (index !== tagIndex) {
							memo.push(key);
						}

						return memo;
					},
					[preparedTags[tagIndex]]
				);
			}
		}

		return {
			visibleList: preparedTags.slice(0, numberVisibleTags),
			hiddenList: preparedTags.slice(numberVisibleTags)
		};
	}, [numberVisibleTags, selectedTag, tags]);

	const onClickMoreButton = useCallback(() => {
		setIsShowModal(true);
	}, []);

	const onCloseModal = useCallback(() => {
		setIsShowModal(false);
	}, []);

	useEffect(() => {
		setIsShowModal(false);

		const tagActiveIndex = tagsByVisibility?.visibleList?.findIndex(
			(tag) => tag.name === selectedTag
		);

		if (tagActiveIndex !== -1 || tagActiveIndex !== 0) {
			setSlideActiveIndex(tagActiveIndex);
		} else {
			setSlideActiveIndex(0);
		}
	}, [asPath, tagsByVisibility]);

	const sliderParams = useMemo(
		() => ({
			slidesPerView: 'auto',
			spaceBetween: 8,
			breakpoints: false,
			navigation: false,
			onReachBeginning: () => {
				setShowLeftGradient(false);
			},
			onReachEnd: () => {
				setShowRightGradient(false);
			},
			onLock: () => {
				setShowRightGradient(false);
				setShowLeftGradient(false);
			},
			onUnlock: () => {
				setShowRightGradient(true);
				setShowLeftGradient(true);
			},
			onSetTranslate: (swiper, translate) => {
				if (translate) {
					setShowLeftGradient(true);
				} else {
					setShowLeftGradient(false);
				}

				if (translate > swiper.maxTranslate()) {
					setShowRightGradient(true);
				} else {
					setShowRightGradient(false);
				}
			}
		}),
		[]
	);

	const tagsCx = cx(styles.FilterTags, {
		[styles.FilterTags_showLeftGradient]: showLeftGradient,
		[styles.FilterTags_showRightGradient]: showRightGradient
	});

	return (
		<div className={tagsCx}>
			<BaseSlider slideToNumber={slideActiveIndex} params={sliderParams}>
				<Tag title="Все теги" next active={!selectedTag} url={basePath} />
				{_(tagsByVisibility.visibleList).map((tag) => (
					<Tag
						key={tag.name}
						title={tag.title}
						name={tag.name}
						active={tag.name === selectedTag}
						url={tag.url}
						next
					/>
				))}
				{tags.length > numberVisibleTags && (
					<Tag
						title={titleMoreButton}
						onClick={onClickMoreButton}
						theme="more"
					/>
				)}
			</BaseSlider>
			{isShowModal && (
				<TagsModal
					tags={tagsByVisibility.hiddenList}
					isShowModal={isShowModal}
					onClose={onCloseModal}
				/>
			)}
		</div>
	);
};

FilterTags.propTypes = {
	tags: PropTypes.array.isRequired,
	selectedTag: PropTypes.string,
	basePath: PropTypes.string,
	asPath: PropTypes.string,
	numberVisibleTags: PropTypes.number,
	titleMoreButton: PropTypes.string
};

FilterTags.defaultProps = {
	numberVisibleTags: 10,
	titleMoreButton: 'Ещё...'
};

export default FilterTags;
